import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, X, ChevronDown, ChevronRight, EyeOff, Undo2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { StockPOPanel, ConsumeFromStockPanel, StockRequestsPanel } from "./purchase-board/StockPanels";

const FIELD = "w-full border border-slate-300 rounded-sm px-2 py-1.5 text-sm";
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/** Iter77 — Central "raise a PO" hub on the Purchase Board.
 * Lists every project that has accessories on its latest quote OR any open
 * production purchase-requests. Each project is a collapsible card. Inside,
 * the user ticks the items they want on the PO, picks a vendor (with the
 * inline "add new vendor" affordance from iter62), enters extras, and hits
 * Create. Uses the same `POST /costing/project/{pid}/purchase-orders`
 * endpoint that project-side PO creation used before.
 */
export default function PurchaseBoardCreatePO({ onCreated }) {
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPid, setOpenPid] = useState(null);
  const [q, setQ] = useState("");
  const [showHidden, setShowHidden] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: v }] = await Promise.all([
        api.get(`/costing/purchase-board/projects${showHidden ? "?show_hidden=1" : ""}`),
        api.get("/costing/vendors"),
      ]);
      setProjects(b.projects || []);
      setVendors(v || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showHidden]);

  const skipPO = async (p) => {
    if (!window.confirm(`Skip PO for ${p.project_pid}? It will be hidden from the Purchase Board.`)) return;
    try {
      await api.post(`/costing/purchase-board/projects/${p.project_id}/skip`, {});
      toast.success(`${p.project_pid} moved to "PO not required"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const unskipPO = async (p) => {
    try {
      await api.post(`/costing/purchase-board/projects/${p.project_id}/unskip`, {});
      toast.success(`${p.project_pid} reactivated on Purchase Board`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = projects.filter((p) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return [p.project_pid, p.name].filter(Boolean).some((s) => String(s).toLowerCase().includes(needle));
  });

  const refreshVendors = async () => {
    const { data } = await api.get("/costing/vendors");
    setVendors(data || []);
  };

  const [seedLines, setSeedLines] = useState(null);

  return (
    <div className="space-y-3" data-testid="pb-create-po-wrapper">
      <StockRequestsPanel onSelect={(seeds) => setSeedLines(seeds)} />
      <StockPOPanel vendors={vendors} refreshVendors={refreshVendors}
        seedLines={seedLines}
        onCreated={() => { setSeedLines(null); load(); onCreated && onCreated(); }} />
      <div className="bg-white border border-slate-200 rounded-sm" data-testid="pb-create-po-panel">
      <div className="p-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="hk-tiny-label">Raise Purchase Orders</div>
          <div className="text-xs text-slate-500">
            Projects with quote accessories or open purchase requests. Tick the
            items, pick a vendor, hit Create.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 select-none cursor-pointer"
            data-testid="pb-show-hidden-toggle">
            <input type="checkbox" checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)} />
            Show hidden
          </label>
          <input className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs w-64"
            placeholder="Filter by project id or name" value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="pb-create-filter" />
        </div>
      </div>
      {loading ? (
        <div className="p-6 text-center text-slate-400 text-xs">Loading projects…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-slate-400 text-xs" data-testid="pb-create-empty">
          No projects with accessories or purchase requests found.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {filtered.map((p) => (
            <ProjectRow key={p.project_id} p={p} vendors={vendors}
              open={openPid === p.project_id}
              onToggle={() => setOpenPid(openPid === p.project_id ? null : p.project_id)}
              onCreated={() => { load(); onCreated && onCreated(); }}
              onSkip={() => skipPO(p)}
              onUnskip={() => unskipPO(p)}
              refreshVendors={refreshVendors} />
          ))}
        </ul>
      )}
      </div>
    </div>
  );
}

function ProjectRow({ p, vendors, open, onToggle, onCreated, onSkip, onUnskip, refreshVendors }) {
  const hidden = p.po_required === false;
  return (
    <li className="text-xs" data-testid={`pb-project-${p.project_id}`}>
      <div className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 ${hidden ? "bg-slate-50/70" : "hover:bg-slate-50"}`}>
        <button type="button" onClick={onToggle}
          className="flex items-center gap-2 min-w-0 flex-1 text-left">
          {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
          <div className="min-w-0">
            <div className="font-bold text-slate-900 font-mono truncate flex items-center gap-1.5">
              {p.project_pid}
              {hidden && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase bg-slate-200 text-slate-600"
                  data-testid={`pb-hidden-badge-${p.project_id}`}>
                  <EyeOff className="w-2.5 h-2.5" /> PO Skipped
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 truncate">{p.name || "—"}</div>
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-[10px] text-slate-500 font-mono flex flex-col items-end leading-tight">
            <span data-testid={`pb-project-counts-open-${p.project_id}`}>
              {p.accessories.length} accessor{p.accessories.length === 1 ? "y" : "ies"}
              {" · "}
              {p.purchase_requests.length} request{p.purchase_requests.length === 1 ? "" : "s"}
            </span>
            <span className="text-[10px]" data-testid={`pb-project-counts-summary-${p.project_id}`}>
              <span className={p.po_raised_count > 0 ? "text-emerald-700 font-bold" : "text-slate-400"}>
                {p.po_raised_count || 0} PO{p.po_raised_count === 1 ? "" : "s"} raised
              </span>
              {" · "}
              <span className={(p.items_left ?? 0) > 0 ? "text-amber-700 font-bold" : "text-slate-400"}>
                {p.items_left ?? 0} item{p.items_left === 1 ? "" : "s"} left
              </span>
            </span>
          </div>
          {hidden ? (
            <button type="button" onClick={onUnskip}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-slate-200 hover:bg-slate-300 text-slate-700 text-[10px] font-bold uppercase"
              data-testid={`pb-unskip-${p.project_id}`}>
              <Undo2 className="w-3 h-3" /> Reactivate
            </button>
          ) : (
            <button type="button" onClick={onSkip}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-slate-300 hover:bg-slate-100 text-slate-600 text-[10px] font-bold uppercase"
              data-testid={`pb-skip-${p.project_id}`}>
              <EyeOff className="w-3 h-3" /> Skip PO
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="p-3 bg-slate-50 border-t border-slate-100 space-y-2">
          <ConsumeFromStockPanel project={p} onIssued={onCreated} />
          <POForm p={p} vendors={vendors} onCreated={onCreated}
            refreshVendors={refreshVendors} />
        </div>
      )}
    </li>
  );
}

function POForm({ p, vendors, onCreated, refreshVendors }) {
  const emptyLine = () => ({ item_name: "", quantity: "", unit: "", unit_price: "", hsn: "" });
  const [form, setForm] = useState({
    vendor_id: "", delivery_date: "", notes: "",
    lines: [emptyLine()], extras: [],
  });
  const [saving, setSaving] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [vendorDraft, setVendorDraft] = useState({ name: "", gst: "", phone: "", email: "", address: "" });
  const [savingVendor, setSavingVendor] = useState(false);

  const addLineFromAccessory = (a) => {
    setForm((f) => ({
      ...f, lines: [
        // strip any blank starter line so ticks map 1:1
        ...f.lines.filter((ln) => ln.item_name.trim()),
        { item_name: a.name, quantity: String(a.qty || ""), unit: "",
          unit_price: a.rate ? String(a.rate) : "", hsn: "" },
      ],
    }));
  };
  const addLineFromRequest = (r) => {
    setForm((f) => ({
      ...f, lines: [
        ...f.lines.filter((ln) => ln.item_name.trim()),
        { item_name: r.item_name || r.item, quantity: String(r.quantity || ""),
          unit: r.unit || "", unit_price: "", hsn: "",
          request_id: r.id },
      ],
    }));
  };
  const setLine = (i, field, val) => {
    setForm((f) => ({ ...f, lines: f.lines.map((ln, idx) => idx === i ? { ...ln, [field]: val } : ln) }));
  };
  const rmLine = (i) => setForm((f) => ({
    ...f, lines: f.lines.length > 1 ? f.lines.filter((_, idx) => idx !== i) : [emptyLine()],
  }));
  const addBlankLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));

  const addExtra = (preset) => setForm((f) => ({
    ...f, extras: [...(f.extras || []), preset || { label: "", amount: "" }],
  }));
  const setExtra = (i, field, val) => setForm((f) => ({
    ...f, extras: f.extras.map((e, idx) => (idx === i ? { ...e, [field]: val } : e)),
  }));
  const rmExtra = (i) => setForm((f) => ({
    ...f, extras: f.extras.filter((_, idx) => idx !== i),
  }));

  const submit = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor"); return; }
    const cleaned = form.lines
      .filter((ln) => ln.item_name.trim() && Number(ln.quantity) > 0 && Number(ln.unit_price) >= 0)
      .map((ln) => ({
        item_name: ln.item_name.trim(),
        quantity: Number(ln.quantity),
        unit: ln.unit || null,
        unit_price: Number(ln.unit_price),
        hsn: ln.hsn || null,
        request_id: ln.request_id || null,
      }));
    if (cleaned.length === 0) { toast.error("Add at least one valid line"); return; }
    const extrasClean = (form.extras || [])
      .map((x) => ({ label: (x.label || "").trim(), amount: Number(x.amount) || 0 }))
      .filter((x) => x.label && (x.amount || x.amount === 0));
    setSaving(true);
    try {
      await api.post(`/costing/project/${p.project_id}/purchase-orders`, {
        vendor_id: form.vendor_id,
        delivery_date: form.delivery_date || null,
        notes: form.notes || null,
        lines: cleaned,
        extras: extrasClean,
      });
      toast.success(`PO created for ${p.project_pid}`);
      setForm({ vendor_id: "", delivery_date: "", notes: "", lines: [emptyLine()], extras: [] });
      onCreated && onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const submitVendor = async () => {
    const name = (vendorDraft.name || "").trim();
    if (!name) { toast.error("Vendor name is required"); return; }
    setSavingVendor(true);
    try {
      const { data } = await api.post("/costing/vendors", {
        name,
        gst: vendorDraft.gst || null,
        phone: vendorDraft.phone || null,
        email: vendorDraft.email || null,
        address: vendorDraft.address || null,
      });
      toast.success(`Vendor "${name}" created`);
      setVendorDraft({ name: "", gst: "", phone: "", email: "", address: "" });
      setShowVendorForm(false);
      await refreshVendors();
      setForm((f) => ({ ...f, vendor_id: data.id }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingVendor(false); }
  };

  const lineTotal = (ln) => (Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0);
  const subtotal = form.lines.reduce((s, ln) => s + lineTotal(ln), 0);
  const extrasTotal = (form.extras || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
  const total = subtotal + extrasTotal;
  const isPicked = (name) => form.lines.some((ln) => ln.item_name === name);

  return (
    <div className="grid md:grid-cols-2 gap-3">
      {/* LEFT: pickable items */}
      <div className="space-y-3">
        {p.accessories.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-2">
            <div className="hk-tiny-label mb-1">Accessories from quote</div>
            <ul className="space-y-1" data-testid="pb-accessories-list">
              {p.accessories.map((a) => (
                <li key={a.accessory_id || a.name}
                  className="flex items-center justify-between gap-2 text-[11px] p-1 hover:bg-slate-50 rounded-sm">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{a.name}</div>
                    <div className="text-[10px] text-slate-500">
                      {a.brand}{a.model_number ? ` · ${a.model_number}` : ""}
                      {a.qty ? ` · qty ${a.qty}` : ""}
                      {a.rate ? ` · ${inr(a.rate)}` : ""}
                    </div>
                  </div>
                  <button type="button" onClick={() => addLineFromAccessory(a)}
                    disabled={isPicked(a.name)}
                    className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900 disabled:opacity-40 shrink-0"
                    data-testid={`pb-pick-acc-${a.accessory_id || a.name}`}>
                    {isPicked(a.name) ? "Added" : "Add"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
        {p.purchase_requests.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-2">
            <div className="hk-tiny-label mb-1">Production purchase requests</div>
            <ul className="space-y-1" data-testid="pb-requests-list">
              {p.purchase_requests.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 text-[11px] p-1 hover:bg-slate-50 rounded-sm">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{r.item_name || r.item}</div>
                    <div className="text-[10px] text-slate-500">
                      qty {r.quantity}{r.unit ? ` ${r.unit}` : ""}
                      {r.notes ? ` · ${r.notes}` : ""}
                    </div>
                  </div>
                  <button type="button" onClick={() => addLineFromRequest(r)}
                    disabled={form.lines.some((ln) => ln.request_id === r.id)}
                    className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900 disabled:opacity-40 shrink-0"
                    data-testid={`pb-pick-req-${r.id}`}>
                    {form.lines.some((ln) => ln.request_id === r.id) ? "Added" : "Add"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* RIGHT: PO composer */}
      <div className="space-y-2">
        <div className="flex items-center gap-1">
          <select className={FIELD + " flex-1"} value={form.vendor_id}
            onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
            data-testid="pb-po-vendor">
            <option value="">Select vendor…</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <button type="button" onClick={() => setShowVendorForm((v) => !v)}
            className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900 shrink-0 px-2"
            data-testid="pb-po-vendor-new-toggle">
            {showVendorForm ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          </button>
        </div>
        {showVendorForm && (
          <div className="border border-orange-200 bg-orange-50 rounded-sm p-2 space-y-1" data-testid="pb-po-vendor-new-form">
            <div className="text-[10px] uppercase font-bold text-orange-700">New vendor</div>
            <input className={FIELD} placeholder="Vendor name (required)"
              value={vendorDraft.name}
              onChange={(e) => setVendorDraft({ ...vendorDraft, name: e.target.value })} />
            <div className="grid grid-cols-2 gap-1">
              <input className={FIELD} placeholder="GST" value={vendorDraft.gst}
                onChange={(e) => setVendorDraft({ ...vendorDraft, gst: e.target.value })} />
              <input className={FIELD} placeholder="Phone" value={vendorDraft.phone}
                onChange={(e) => setVendorDraft({ ...vendorDraft, phone: e.target.value })} />
            </div>
            <input className={FIELD} placeholder="Email" value={vendorDraft.email}
              onChange={(e) => setVendorDraft({ ...vendorDraft, email: e.target.value })} />
            <input className={FIELD} placeholder="Address" value={vendorDraft.address}
              onChange={(e) => setVendorDraft({ ...vendorDraft, address: e.target.value })} />
            <button type="button" onClick={submitVendor} disabled={savingVendor || !vendorDraft.name.trim()}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-2 py-1.5 rounded-sm text-[11px] uppercase">
              {savingVendor ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Save vendor
            </button>
          </div>
        )}
        <input className={FIELD} type="date" placeholder="Delivery date"
          value={form.delivery_date}
          onChange={(e) => setForm({ ...form, delivery_date: e.target.value })} />

        <div className="space-y-1" data-testid="pb-po-lines">
          {form.lines.map((ln, i) => (
            <div key={i} className="border border-slate-100 rounded-sm p-1 space-y-1 bg-white">
              <input className={FIELD} placeholder="Item name" value={ln.item_name}
                onChange={(e) => setLine(i, "item_name", e.target.value)}
                data-testid={`pb-po-line-name-${i}`} />
              <div className="grid grid-cols-4 gap-1">
                <input className={FIELD} type="number" placeholder="Qty" value={ln.quantity}
                  onChange={(e) => setLine(i, "quantity", e.target.value)} />
                <input className={FIELD} placeholder="Unit" value={ln.unit}
                  onChange={(e) => setLine(i, "unit", e.target.value)} />
                <input className={FIELD} type="number" step="0.01" placeholder="Rate" value={ln.unit_price}
                  onChange={(e) => setLine(i, "unit_price", e.target.value)} />
                <input className={FIELD} placeholder="HSN" value={ln.hsn}
                  onChange={(e) => setLine(i, "hsn", e.target.value)} />
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-500">
                <span>Line total: <b>{inr(lineTotal(ln))}</b></span>
                <button type="button" onClick={() => rmLine(i)}
                  className="text-red-600 hover:text-red-800">Remove</button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addBlankLine}
          className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900">
          + Add blank line
        </button>

        <div className="pt-1 border-t border-slate-200">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase font-bold text-slate-500">Charges & taxes</div>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => addExtra({ label: "Tax", amount: "" })}
                className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900">+ Tax</button>
              <button type="button" onClick={() => addExtra({ label: "Transport", amount: "" })}
                className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900">+ Transport</button>
              <button type="button" onClick={() => addExtra()}
                className="text-[10px] font-bold uppercase text-orange-700 hover:text-orange-900">+ Other</button>
            </div>
          </div>
          {(form.extras || []).map((ex, i) => (
            <div key={i} className="flex items-center gap-1 mb-1">
              <input className={FIELD + " flex-1"} placeholder="Label" value={ex.label}
                onChange={(e) => setExtra(i, "label", e.target.value)} />
              <input className={FIELD + " w-28"} type="number" step="0.01" placeholder="Amount"
                value={ex.amount}
                onChange={(e) => setExtra(i, "amount", e.target.value)} />
              <button type="button" onClick={() => rmExtra(i)}
                className="text-red-600 hover:text-red-800 shrink-0 px-1">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>

        <textarea className={FIELD} rows={2} placeholder="Notes (optional)"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <div className="text-[11px] text-right text-slate-600">Subtotal: {inr(subtotal)}</div>
        {(form.extras || []).length > 0 && (
          <div className="text-[11px] text-right text-slate-600">Extras: {inr(extrasTotal)}</div>
        )}
        <div className="text-xs font-bold text-right text-slate-900">Total: {inr(total)}</div>
        <button type="button" onClick={submit} disabled={saving || !form.vendor_id}
          className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-bold px-3 py-2 rounded-sm text-xs"
          data-testid="pb-po-submit">
          {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Create PO
        </button>
      </div>
    </div>
  );
}
